import TextField from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState, useEffect } from "react";
import EntityName, { InvalidEntityNameError } from "legal-entities/entity-names/entity-name";


const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minWidth: theme.spacing(40)
}));

type EntityNameInputProps = {
  idPrefix?: string;
  className?: string;
  entityName?: EntityName;
  label?: string;
  variant?: TextFieldVariants;
  onChange?: (entityName?: EntityName, error?: string) => void;
};

export default function EntityNameInput(props: Readonly<EntityNameInputProps>) {
  const { idPrefix, className, label, variant, entityName, onChange } = props;
  const [entityNameFieldText, setEntityNameFieldText] = useState<string>(entityName?.value ?? '');
  const [entityNameFieldError, setEntityNameFieldError] = useState<string>('');

  useEffect(() => {
    setEntityNameFieldText(entityName?.value ?? '');
  }, [entityName]);

  function buildEntityName(inputText?: string): EntityName | undefined {
    try {
      if (!inputText) throw new InvalidEntityNameError("Entity name is required");
      return new EntityName(inputText);
    } catch (error) {
      if (error instanceof InvalidEntityNameError) {
        throw error;
      }
    }
  }

  function handleEntityNameChanged(event: React.ChangeEvent<HTMLInputElement>) {
    setEntityNameFieldText(event.target.value);

    let newEntityName: EntityName | undefined;
    let newErrors: string = '';

    try {
      newEntityName = buildEntityName(event.target.value);
      setEntityNameFieldError('');
    } catch (error) {
      if (!(error instanceof InvalidEntityNameError)) return;
      newErrors = error.message;
      setEntityNameFieldError(newErrors);
    } finally {
      onChange?.(newEntityName, newErrors);
    }
  }

  return (
    <InputField
      id={idPrefix ? idPrefix + "-entity-name-input" : "entity-name-input"}
      className={className}
      label={label ?? "Entity Name"}
      onChange={handleEntityNameChanged}
      required
      value={entityNameFieldText}
      error={!!entityNameFieldError}
      helperText={entityNameFieldError}
      variant={variant ?? "outlined"}
      autoComplete="organization"
    />
  );
}
